import { Component, ElementRef, Input, OnDestroy, OnInit } from '@angular/core';
import { AbstractControl, FormArray, FormBuilder, FormControl, FormGroup, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { MODAL_WIDTH_SMALL } from '../../models/constants';
import {
  ConfigForm,
  CUSTOM_FIELD_DATA_TYPE,
  CUSTOM_FIELD_TARGET,
  CustomField,
  CustomFieldDefinition,
  CustomFieldDefinitionOption,
  PERMACTION,
} from '../../models/main';
import { ApiService } from '../../services/api.service';
import { CustomFieldDefinitionsService } from '../../services/custom-field-definitions.service';
import { FormBase } from '../../shared/form-base';
import { CustomFieldDefinitionModalComponent, CustomFieldDefinitionModalData } from '../custom-field-definition-modal/custom-field-definition-modal.component';

const OTHER_TYPE = 'other';
const ISO_DATE_REGEX = /^\d{4}-\d{2}-\d{2}$/;
const NUMBER_REGEX = /^-?(\d+(\.\d*)?|\.\d+)$/;
/** Controles `value` que ya convierten a null el vacio de los tipos no textuales */
const NORMALIZED_VALUE_CONTROLS = new WeakSet<AbstractControl>();
const CUSTOM_FIELDS_KEY = 'customFields';
const NO_OPTIONS: CustomFieldDefinitionOption[] = [];

/** Icono que representa cada tipo de dato */
const DATA_TYPE_ICONS: Record<CUSTOM_FIELD_DATA_TYPE, string> = {
  [CUSTOM_FIELD_DATA_TYPE.Text]: 'file-text',
  [CUSTOM_FIELD_DATA_TYPE.Number]: 'hash',
  [CUSTOM_FIELD_DATA_TYPE.Date]: 'calendar',
  [CUSTOM_FIELD_DATA_TYPE.Boolean]: 'circle-check',
  [CUSTOM_FIELD_DATA_TYPE.Select]: 'menu',
};

/** Opciones de un `select` calculadas para un campo, con las opciones de la definicion y el valor de los que salen */
interface FieldOptionsCacheEntry {
  options: CustomFieldDefinitionOption[];
  value: string;
  result: CustomFieldDefinitionOption[];
}

/**
 * Entidad cuyos custom fields se editan, para ofrecer las definiciones del catalogo que le aplican. Sin ella (perfil de
 * organizacion, favoritos, plantillas) solo hay campos libres.
 */
export interface CustomFieldsScope {
  target: CUSTOM_FIELD_TARGET;
  recordId: string;
  /** Subtipo del actor, en `actor`: las definiciones pueden limitarse a algunos subtipos */
  actorSubtype?: string;
  /** Tipo del lugar, en `place`: las definiciones pueden limitarse a algunos tipos */
  placeType?: string;
}

/**
 * Custom fields de cualquier entidad (organizacion, actor, place, factura, mercancia, eDoc...). Tres modos:
 * - `conf`: crea su propio FormArray, lo registra como `customFields` en el formulario padre y se engancha a su `onSubmit`.
 * - `array`: edita un FormArray que ya existe en el formulario padre (p. ej. una fila de mercancia creada con createDeepForm).
 * - `fields`: solo lectura.
 *
 * El valor de cada campo es siempre un string, sea cual sea su `dataType` (fecha `YYYY-MM-DD`, booleano `true`/`false`).
 * Los controles no textuales no se enlazan con formControl sino que escriben el string ya serializado, asi el valor del
 * formulario es valido para la API en todo momento, aunque el padre no ejecute los handlers de `onSubmit`.
 *
 * Hay tres clases de campo: libre (`type` `other`, con nombre y tipo de dato elegidos por el usuario), predefinido
 * (`type` de {@link selectOptionsByType}, siempre texto) y de definicion del catalogo (`definition`). Cada fila muestra
 * el nombre como etiqueta, con el icono de su tipo de dato, y el valor debajo. El nombre de un campo libre se edita en
 * un input hasta que se confirma, y su icono de tipo abre un menu para cambiarlo; en el resto el icono es informativo.
 *
 * Con `scope`, al anadir un campo se ofrecen las definiciones del catalogo aplicables: un campo con definicion fija su
 * nombre, tipo y opciones. Los `select` guardan la `key` de la opcion y muestran su `label`, y no ofrecen las opciones
 * archivadas salvo la del valor actual. Las definiciones `alwaysShow` aparecen como filas vacias aunque la entidad no
 * las tenga, y como cualquier fila vacia no se envian. Un campo de definicion archivada sigue editandose con normalidad,
 * marcado con un icono de archivado. Un administrador de la organizacion del expediente puede guardar un campo libre
 * como definicion nueva.
 */
@Component({
  selector: 'app-custom-fields',
  templateUrl: './custom-fields.component.html',
  styleUrls: ['./custom-fields.component.scss'],
})
export class CustomFieldsComponent extends FormBase implements OnInit, OnDestroy {

  @Input() conf?: ConfigForm<CustomField[] | undefined>;
  @Input() array?: FormArray;
  @Input() fields?: CustomField[];
  @Input() type: 'organization' | 'actor' | 'custom' = 'custom';
  @Input() scope?: CustomFieldsScope;

  DATA_TYPE = CUSTOM_FIELD_DATA_TYPE;
  /** Decimales admitidos por el input numerico; la API acepta cualquier numero decimal */
  numberDecimals = 10;
  DATA_TYPE_ICONS = DATA_TYPE_ICONS;
  /** El tipo `select` necesita opciones, que solo vienen del catalogo de definiciones: no se ofrece en los campos libres */
  freeDataTypes = [CUSTOM_FIELD_DATA_TYPE.Text, CUSTOM_FIELD_DATA_TYPE.Number, CUSTOM_FIELD_DATA_TYPE.Date, CUSTOM_FIELD_DATA_TYPE.Boolean];
  options: string[] = [];
  form: FormArray = new FormArray<any>([]);
  /** Definiciones no archivadas aplicables a la entidad: las que se ofrecen al anadir un campo */
  definitions: CustomFieldDefinition[] = [];
  /** Todas las definiciones conocidas por id, incluidas las archivadas, para mostrar los campos que ya las usan */
  definitionsById = new Map<string, CustomFieldDefinition>();
  canCreateDefinitions = false;
  private destroy = new Subject<void>();
  /** Resultado de {@link fieldOptions} por campo, para devolver el mismo array mientras no cambien sus datos */
  private fieldOptionsCache = new WeakMap<FormGroup, FieldOptionsCacheEntry>();
  /** Ultima fecha valida de cada control `value`, la que se pasa al datepicker */
  private datepickerValues = new WeakMap<AbstractControl, string | null>();
  /** Campos libres cuyo nombre se esta editando: los nuevos o sin nombre, y los que se reabren con el icono de editar */
  private editingName = new WeakSet<FormGroup>();

  /** Tipos predefinidos que se ofrecen al anadir un campo. Los guardados con `other` son campos libres */
  selectOptionsByType: Record<'organization' | 'actor' | 'custom', string[]> = {
    organization: ['guarantee', 'rex', 'originCertificate'],
    actor: ['containerId', 'containerType', 'summary', 'depositReturn', 'description'],
    custom: [],
  };

  constructor(
    private fb: FormBuilder,
    private selfElement: ElementRef,
    private api: ApiService,
    private definitionsService: CustomFieldDefinitionsService,
    private dialog: MatDialog,
  ) { super(); }

  get readonly(): boolean {
    return !this.conf && !this.array;
  }

  ngOnInit(): void {
    this.options = this.selectOptionsByType[this.type];
    this.initForm();
    // Despues de montar el formulario: la respuesta puede venir de la cache de forma sincrona y necesita los campos
    this.loadDefinitions();
  }

  private initForm(): void {
    if (this.array) {
      this.form = this.array;
      this.form.controls.forEach(control => {
        CustomFieldsComponent.normalizeGroup(control as FormGroup);
        this.startEditingUnnamed(control as FormGroup);
      });
      return;
    }
    if (!this.conf) return;

    const conf = this.conf;
    // Sin fila vacia por defecto: una entidad nueva solo muestra las definiciones `alwaysShow`, que llegan con el catalogo
    this.form = this.fb.array((conf.data ?? []).map(elem => this.customFieldFormFactory(elem)));
    this.form.controls.forEach(control => this.startEditingUnnamed(control as FormGroup));
    if (!conf.permissions?.includes(PERMACTION.update)) this.form.disable({ emitEvent: false });

    // Para evitar 'NG0100: Expression has changed after it was checked' https://angular.io/errors/NG0100
    Promise.resolve(null).then(() => conf.form.addControl('customFields', this.form));
    conf.onSubmit.handlers.push({ cb: this.removeEmpty.bind(this, this.form), self: this.selfElement.nativeElement });
    conf.onSubmit.validators.push({ cb: this.validate.bind(this, this.form), self: this.selfElement.nativeElement });
  }

  ngOnDestroy(): void {
    this.destroy.next();
    this.destroy.complete();
    // En modo `array` el padre envia el FormArray sin pasar por `onSubmit`: las filas vacias se quitan al plegar el panel
    if (this.array && this.array.enabled) this.removeEmpty(this.array);
    if (!this.conf) return;
    this.conf.form.removeControl('customFields');
    this.conf.onSubmit.handlers = this.conf.onSubmit.handlers.filter(handler => handler.self !== this.selfElement.nativeElement);
    this.conf.onSubmit.validators = this.conf.onSubmit.validators.filter(handler => handler.self !== this.selfElement.nativeElement);
  }

  /** Completa los controles que puede no traer un campo creado fuera del componente (p. ej. datos antiguos sin `dataType`) */
  static normalizeGroup(group: FormGroup): void {
    // Se lee antes de tocar el grupo: anadir un control habilitado recalcula el estado y rehabilita un grupo deshabilitado
    const wasDisabled = group.disabled;
    if (!group.contains('type')) group.addControl('type', new FormControl(OTHER_TYPE), { emitEvent: false });
    if (!group.contains('value')) group.addControl('value', new FormControl(null), { emitEvent: false });
    if (group.get('type')?.value === OTHER_TYPE && !group.contains('name')) group.addControl('name', new FormControl(''), { emitEvent: false });
    if (!group.contains('dataType')) group.addControl('dataType', new FormControl(CUSTOM_FIELD_DATA_TYPE.Text), { emitEvent: false });
    else if (!group.get('dataType')?.value) group.get('dataType')?.setValue(CUSTOM_FIELD_DATA_TYPE.Text, { emitEvent: false });
    if (wasDisabled) group.disable({ emitEvent: false });
    CustomFieldsComponent.emptyTypedValueToNull(group);
    CustomFieldsComponent.addValueValidator(group);
  }

  /** Marca en el propio control un valor que no encaja con su tipo, para senalarlo en el formulario antes de enviar */
  static addValueValidator(group: FormGroup): void {
    const value = group.get('value');
    if (!value || value.hasValidator(CustomFieldsComponent.valueValidator)) return;
    value.addValidators(CustomFieldsComponent.valueValidator);
    value.updateValueAndValidity({ emitEvent: false });
  }

  /** Validador del control `value`: lee el `dataType` de su grupo */
  static valueValidator: ValidatorFn = (control: AbstractControl): ValidationErrors | null => {
    const dataType = control.parent?.get('dataType')?.value;
    return CustomFieldsComponent.isValidValue(control.value, dataType) ? null : { customFieldInvalidValue: true };
  };

  /**
   * Prepara los custom fields de un formulario que se envia sin pasar por `onSubmit` (p. ej. las filas de mercancia, que
   * van en el payload del recurso padre): quita las filas vacias de todos los `customFields` que contiene y valida el
   * resto, marcando los valores incorrectos en el formulario. Devuelve false, avisando al usuario, si alguno no es valido.
   */
  static prepareNested(control: AbstractControl, api: ApiService): boolean {
    const arrays = CustomFieldsComponent.findCustomFieldArrays(control).filter(array => array.enabled);
    arrays.forEach(array => {
      CustomFieldsComponent.removeEmpty(array);
      array.controls.forEach(group => CustomFieldsComponent.normalizeGroup(group as FormGroup));
    });
    return arrays.every(array => CustomFieldsComponent.validate(array, api));
  }

  /**
   * Como {@link prepareNested}, sobre datos ya extraidos de un formulario que no esta en pantalla (p. ej. el paso de
   * transporte de la extraccion IA). Quita las filas vacias en el propio objeto y devuelve false si algun valor no es valido.
   */
  static prepareNestedData(data: unknown, api: ApiService): boolean {
    const arrays = CustomFieldsComponent.findCustomFieldData(data);
    arrays.forEach(fields => {
      for (let i = fields.length - 1; i >= 0; i--) {
        if (CustomFieldsComponent.isEmptyField(fields[i])) fields.splice(i, 1);
      }
    });
    const fields = arrays.flat();
    const error = fields.map(field => CustomFieldsComponent.fieldError(field)).find(Boolean);
    if (error) api.logError(error);
    return !error;
  }

  private static findCustomFieldData(data: unknown): CustomField[][] {
    if (Array.isArray(data)) return data.flatMap(item => CustomFieldsComponent.findCustomFieldData(item));
    if (!data || typeof data !== 'object') return [];
    return Object.entries(data).flatMap(([key, value]) => key === CUSTOM_FIELDS_KEY && Array.isArray(value)
      ? [value as CustomField[]]
      : CustomFieldsComponent.findCustomFieldData(value));
  }

  private static findCustomFieldArrays(control: AbstractControl): FormArray[] {
    if (control instanceof FormGroup) {
      return Object.entries(control.controls).flatMap(([key, child]) => key === CUSTOM_FIELDS_KEY && child instanceof FormArray
        ? [child]
        : CustomFieldsComponent.findCustomFieldArrays(child));
    }
    if (control instanceof FormArray) return control.controls.flatMap(child => CustomFieldsComponent.findCustomFieldArrays(child));
    return [];
  }

  /**
   * El input numerico escribe '' al vaciarlo, y la API rechaza '' en los tipos no textuales (`new Big('')` lanza). Se
   * normaliza a null en el propio control porque en modo `array` el padre envia el valor sin pasar por `onSubmit`.
   */
  static emptyTypedValueToNull(group: FormGroup): void {
    const value = group.get('value');
    // En modo `array` el componente se recrea cada vez que se despliega la fila: una sola suscripcion por control
    if (!value || NORMALIZED_VALUE_CONTROLS.has(value)) return;
    NORMALIZED_VALUE_CONTROLS.add(value);
    value.valueChanges.subscribe(current => {
      const dataType = group.get('dataType')?.value;
      if (current === '' && dataType && dataType !== CUSTOM_FIELD_DATA_TYPE.Text) value.setValue(null, { emitEvent: false });
    });
  }

  /** Serializa al string que espera la API. Vacio es `null` salvo en texto, donde la API acepta cadena vacia */
  static serializeValue(value: unknown, dataType: CUSTOM_FIELD_DATA_TYPE | null | undefined): string | null {
    if (value === null || value === undefined || value === '') return dataType && dataType !== CUSTOM_FIELD_DATA_TYPE.Text ? null : '';
    // El datepicker usa MomentDateAdapter (en UTC), asi que el formateo no desplaza el dia. Una fecha tecleada no valida queda vacia
    if (dataType === CUSTOM_FIELD_DATA_TYPE.Date && typeof value === 'object') {
      const date = value as { isValid(): boolean, format(format: string): string };
      return date.isValid() ? date.format('YYYY-MM-DD') : null;
    }
    return String(value);
  }

  /** Si el valor encaja con su tipo; lo mismo que valida la API, para avisar antes de enviar */
  static isValidValue(value: string | null, dataType: CUSTOM_FIELD_DATA_TYPE | null | undefined): boolean {
    if (value === null) return true;
    switch (dataType) {
      case CUSTOM_FIELD_DATA_TYPE.Number: return NUMBER_REGEX.test(value);
      case CUSTOM_FIELD_DATA_TYPE.Date: return ISO_DATE_REGEX.test(value);
      case CUSTOM_FIELD_DATA_TYPE.Boolean: return value === 'true' || value === 'false';
      case CUSTOM_FIELD_DATA_TYPE.Select: return value.trim() !== '';
      default: return true;
    }
  }

  /** Carga las definiciones aplicables y, si hay campos que apuntan a otras (archivadas), las archivadas */
  private loadDefinitions(): void {
    if (!this.scope) return;
    const { target, recordId, actorSubtype, placeType } = this.scope;
    this.definitionsService.listForRecord(recordId, target).pipe(takeUntil(this.destroy)).subscribe(definitions => {
      this.definitions = definitions
        .filter(definition => !definition.actorSubtypes || !actorSubtype || definition.actorSubtypes.includes(actorSubtype))
        .filter(definition => !definition.placeTypes || !placeType || definition.placeTypes.includes(placeType));
      definitions.forEach(definition => this.definitionsById.set(definition.id, definition));
      this.addAlwaysShownFields();
      const missing = this.fieldDefinitionIds().some(id => !this.definitionsById.has(id));
      if (missing) {
        this.definitionsService.listArchivedForRecord(recordId, target).pipe(takeUntil(this.destroy)).subscribe(archived => {
          archived.forEach(definition => {
            if (!this.definitionsById.has(definition.id)) this.definitionsById.set(definition.id, definition);
          });
        });
      }
    });
    if (!this.readonly) {
      this.definitionsService.canManageForRecord(recordId).pipe(takeUntil(this.destroy)).subscribe(canManage => this.canCreateDefinitions = canManage);
    }
  }

  private fieldDefinitionIds(): string[] {
    const fields: CustomField[] = this.readonly ? this.fields ?? [] : this.form.getRawValue();
    return fields.map(field => field.definition).filter((id): id is string => !!id);
  }

  /**
   * Anade vacias las definiciones `alwaysShow` que la entidad aun no tiene. Sin marcar el formulario ni emitir cambios:
   * no son una edicion del usuario, y si no se rellenan se quitan al guardar como cualquier fila vacia.
   */
  private addAlwaysShownFields(): void {
    if (this.readonly || this.form.disabled) return;
    const used = new Set(this.fieldDefinitionIds());
    this.definitions.filter(definition => definition.alwaysShow && !used.has(definition.id)).forEach(definition => {
      this.form.push(this.definitionFieldFormFactory(definition, CustomFieldsComponent.serializeValue(null, definition.dataType)), { emitEvent: false });
    });
  }

  /** Definiciones que se pueden anadir: las aplicables que aun no tiene la entidad */
  get availableDefinitions(): CustomFieldDefinition[] {
    const used = new Set(this.fieldDefinitionIds());
    return this.definitions.filter(definition => !used.has(definition.id));
  }

  definitionOf(customField: FormGroup | CustomField): CustomFieldDefinition | undefined {
    const id = customField instanceof FormGroup ? customField.get('definition')?.value : customField.definition;
    return id ? this.definitionsById.get(id) : undefined;
  }

  /** Campo libre: sin definicion y sin tipo predefinido. Los datos antiguos con `other` lo son */
  isFreeField(customField: FormGroup): boolean {
    return !customField.get('definition')?.value && customField.get('type')?.value === OTHER_TYPE;
  }

  /**
   * Definicion archivada: ya no se ofrece al anadir campos, pero el campo que ya la tiene se edita con normalidad. Solo
   * se marca con un icono
   */
  isArchivedDefinition(customField: FormGroup | CustomField): boolean {
    return !!this.definitionOf(customField)?.archived;
  }

  /** Tooltip del icono de archivado de un campo: por su definicion o por la opcion elegida de un `select`, o null */
  archivedTooltip(customField: FormGroup | CustomField): string | null {
    if (this.isArchivedDefinition(customField)) return 'customFieldDefinitions.archivedFieldTooltip';
    const value = customField instanceof FormGroup ? customField.get('value')?.value : customField.value;
    const option = value ? this.definitionOf(customField)?.options?.find(o => o.key === value) : undefined;
    return option?.archived ? 'customFieldDefinitions.archivedOptionTooltip' : null;
  }

  isEditingName(customField: FormGroup): boolean {
    return this.editingName.has(customField);
  }

  editName(customField: FormGroup): void {
    this.editingName.add(customField);
  }

  /** Vuelve a mostrar el nombre como etiqueta. Sin nombre se queda el input, que es obligatorio */
  confirmName(customField: FormGroup): void {
    const name = customField.get('name');
    if (!name?.value?.trim()) {
      name?.markAsTouched();
      return;
    }
    this.editingName.delete(customField);
  }

  private startEditingUnnamed(customField: FormGroup): void {
    if (this.isFreeField(customField) && !customField.get('name')?.value?.trim()) this.editingName.add(customField);
  }

  /** Etiqueta de la fila: el nombre de la definicion, el del campo libre o la traduccion del tipo predefinido */
  rowLabel(customField: FormGroup): { text: string, translate: boolean } {
    if (customField.get('definition')?.value) return { text: this.definitionOf(customField)?.label || customField.get('name')?.value || '', translate: false };
    if (this.isFreeField(customField)) return { text: customField.get('name')?.value || '', translate: false };
    return { text: 'customField.' + customField.get('type')?.value, translate: true };
  }

  dataTypeOf(customField: FormGroup): CUSTOM_FIELD_DATA_TYPE {
    return customField.get('dataType')?.value || CUSTOM_FIELD_DATA_TYPE.Text;
  }

  /** Nombre a mostrar: el de la definicion si se conoce (puede haberse renombrado), si no el guardado en el campo */
  fieldName(field: CustomField): string {
    return this.definitionOf(field)?.label || field.name || '';
  }

  /** Valor a mostrar en solo lectura. Un `select` muestra la etiqueta de la opcion y, si no se conoce, la `key` guardada */
  selectLabel(field: CustomField): string | null {
    return this.definitionOf(field)?.options?.find(option => option.key === field.value)?.label ?? field.value;
  }

  addDefinitionField(definition: CustomFieldDefinition): void {
    // El interruptor de un booleano se ve apagado: se guarda como tal en vez de como vacio
    const value = definition.dataType === CUSTOM_FIELD_DATA_TYPE.Boolean ? 'false' : CustomFieldsComponent.serializeValue(null, definition.dataType);
    this.addFormElement(this.form, this.definitionFieldFormFactory(definition, value));
    this.form.markAsDirty();
  }

  private definitionFieldFormFactory(definition: CustomFieldDefinition, value: string | null): FormGroup {
    const group = this.customFieldFormFactory({ type: OTHER_TYPE, name: definition.label, dataType: definition.dataType, definition: definition.id });
    group.get('value')?.setValue(value);
    return group;
  }

  /** Campo de un tipo predefinido (`containerId`, `rex`...): sin nombre propio y siempre texto */
  addPredefinedField(type: string): void {
    this.addFormElement(this.form, this.customFieldFormFactory({ type, value: '' }));
    this.form.markAsDirty();
  }

  addFreeField(): void {
    const group = this.customFieldFormFactory({ type: OTHER_TYPE, name: '' });
    this.editingName.add(group);
    this.addFormElement(this.form, group);
    this.form.markAsDirty();
  }

  /** Solo los campos libres con nombre se pueden guardar como definicion, y solo si hay entidad a la que asociarla */
  canSaveAsDefinition(customField: FormGroup): boolean {
    return this.canCreateDefinitions && !!this.scope && customField.enabled
      && customField.get('type')?.value === OTHER_TYPE && !customField.get('definition')?.value && !!customField.get('name')?.value?.trim();
  }

  /** Crea una definicion con el nombre y tipo del campo libre y enlaza el campo a ella */
  saveAsDefinition(customField: FormGroup): void {
    if (!this.scope) return;
    const data: CustomFieldDefinitionModalData = {
      initial: { target: this.scope.target, label: customField.get('name')?.value.trim(), dataType: customField.get('dataType')?.value || CUSTOM_FIELD_DATA_TYPE.Text },
      lockTarget: true,
    };
    this.dialog.open(CustomFieldDefinitionModalComponent, { data, ...MODAL_WIDTH_SMALL }).afterClosed().subscribe((definition?: CustomFieldDefinition) => {
      if (!definition) return;
      this.definitionsById.set(definition.id, definition);
      const applies = (!definition.actorSubtypes || !this.scope?.actorSubtype || definition.actorSubtypes.includes(this.scope.actorSubtype))
        && (!definition.placeTypes || !this.scope?.placeType || definition.placeTypes.includes(this.scope.placeType));
      if (applies) this.definitions = [...this.definitions, definition];
      // Si la definicion no aplica a este subtipo de actor o tipo de lugar el back rechazaria el campo enlazado: se queda como libre
      if (!applies) return;
      const value = customField.get('value')?.value;
      customField.patchValue({ name: definition.label, dataType: definition.dataType, definition: definition.id });
      if (!customField.contains('definition')) customField.addControl('definition', new FormControl(definition.id));
      const keepValue = value !== null && value !== '' && CustomFieldsComponent.isValidValue(value, definition.dataType)
        && (definition.dataType !== CUSTOM_FIELD_DATA_TYPE.Select || !!definition.options?.some(option => option.key === value));
      customField.get('value')?.setValue(keepValue ? value : CustomFieldsComponent.serializeValue(null, definition.dataType));
      customField.markAsDirty();
    });
  }

  setValue(customField: FormGroup, value: unknown): void {
    customField.get('value')?.setValue(CustomFieldsComponent.serializeValue(value, customField.get('dataType')?.value));
    customField.markAsDirty();
  }

  /** Al cambiar el tipo de dato el valor anterior deja de ser valido */
  selectDataType(customField: FormGroup, dataType: CUSTOM_FIELD_DATA_TYPE): void {
    if (this.dataTypeOf(customField) === dataType) return;
    customField.get('dataType')?.setValue(dataType);
    customField.get('value')?.setValue(CustomFieldsComponent.serializeValue(null, dataType));
    customField.markAsDirty();
  }

  /** El tipo de dato solo se elige en campos libres que no vienen de una definicion del catalogo */
  canChooseDataType(customField: FormGroup): boolean {
    return this.isFreeField(customField);
  }

  /**
   * Si el valor de un `select` se elige en un desplegable. Con la definicion conocida siempre, aunque no le queden opciones
   * activas: un texto libre lo rechazaria la API. Sin ella (no ha cargado o ya no existe) solo si hay un valor que mostrar.
   */
  showsSelect(customField: FormGroup): boolean {
    return !!this.definitionOf(customField) || this.fieldOptions(customField).length > 0;
  }

  /**
   * Opciones de un `select`. Las archivadas no se ofrecen salvo la del valor actual, que la API acepta mientras no cambie.
   * Un valor guardado que ya no esta entre ellas (o cuya definicion aun no ha cargado) se mantiene para no perderlo al
   * editar. La plantilla lo llama en cada deteccion de cambios: devolver un array nuevo haria que el
   * `*ngFor` recrease los `mat-option`, y `mat-select` reacciona a ese cambio con una microtarea que lanza otra deteccion
   * de cambios, en bucle, bloqueando la pagina. Por eso se devuelve el mismo array mientras no cambien opciones ni valor.
   */
  fieldOptions(customField: FormGroup): CustomFieldDefinitionOption[] {
    const options = this.definitionOf(customField)?.options ?? NO_OPTIONS;
    const value = customField.get('value')?.value;
    const known = !value || options.some(option => option.key === value);
    if (known && !options.some(option => option.archived && option.key !== value)) return options;
    const cached = this.fieldOptionsCache.get(customField);
    if (cached?.options === options && cached.value === value) return cached.result;
    const result = options.filter(option => !option.archived || option.key === value);
    if (!known) result.push({ key: value, label: value });
    this.fieldOptionsCache.set(customField, { options, value, result });
    return result;
  }

  /**
   * Fecha elegida o tecleada en el datepicker, que entrega null tanto si el input esta vacio como si el texto no es una
   * fecha. Un texto no valido se guarda tal cual para que el validador marque el control y bloquee el guardado, en vez de
   * vaciar el valor sin aviso.
   */
  setDateValue(customField: FormGroup, value: unknown, text: string): void {
    if (value === null && text.trim()) {
      customField.get('value')?.setValue(text.trim());
      customField.get('value')?.markAsTouched();
      customField.markAsDirty();
      return;
    }
    this.setValue(customField, value);
  }

  /**
   * Valor que se pasa al datepicker. Un texto no valido se queda en el control pero no se le devuelve: el datepicker lo
   * descartaria y borraria del input lo tecleado. Se mantiene la ultima fecha valida, que ya es la que tiene.
   */
  datepickerValue(customField: FormGroup): string | null {
    const control = customField.get('value');
    if (!control) return null;
    const value: string | null = control.value ?? null;
    if (value !== null && !CustomFieldsComponent.isValidValue(value, CUSTOM_FIELD_DATA_TYPE.Date)) {
      return this.datepickerValues.get(control) ?? null;
    }
    this.datepickerValues.set(control, value);
    return value;
  }

  customFieldFormFactory(data?: any): FormGroup {
    const field = new CustomField(data ?? { type: OTHER_TYPE, name: '' });
    const form: FormGroup = this.fb.group(field);
    form.controls.type.addValidators(Validators.required);
    if (form.controls.name) form.controls.name.addValidators(Validators.required);
    CustomFieldsComponent.emptyTypedValueToNull(form);
    CustomFieldsComponent.addValueValidator(form);
    return form;
  }

  removeEmpty(form: FormArray) {
    CustomFieldsComponent.removeEmpty(form);
  }

  validate(form: FormArray): boolean | void {
    return CustomFieldsComponent.validate(form, this.api);
  }

  static removeEmpty(form: FormArray) {
    // Se recorre de forma inversa para que las eliminaciones no afecten al indice
    for (let i = form.length; i > 0; i--) {
      if (CustomFieldsComponent.isEmptyField((form.get([i - 1]) as FormGroup).getRawValue())) form.removeAt(i - 1);
    }
  }

  /** Sin valor y, ademas, sin tipo, campo libre sin nombre o campo de una definicion (se puede volver a anadir desde el catalogo) */
  private static isEmptyField(data: CustomField): boolean {
    const isEmptyValue = data.value === null || data.value === undefined || data.value === '';
    return isEmptyValue && (!data.type || !!data.definition || (data.type === OTHER_TYPE && !data.name?.trim()));
  }

  private static fieldError(elem: CustomField): string | null {
    if (elem.value && !elem.type) return 'toast.customFieldEmpty';
    if (elem.type === OTHER_TYPE && !elem.name) return 'toast.customFieldNameRequired';
    if (!CustomFieldsComponent.isValidValue(elem.value ?? null, elem.dataType)) return 'toast.customFieldInvalidValue';
    return null;
  }

  static validate(form: FormArray, api: ApiService): boolean {
    form.markAllAsTouched();
    const data: CustomField[] = form.getRawValue();
    const error = data.map(elem => CustomFieldsComponent.fieldError(elem)).find(Boolean);
    if (error) api.logError(error);
    return !error;
  }

}
